import React from 'react';
import SignalBadge from './SignalBadge';
import PriceCard from './PriceCard';

function IndicatorRow({ indicator }) {
  return (
    <tr className="indicator-row">
      <td className="ind-name">{indicator.name}</td>
      <td className="ind-signal"><SignalBadge signal={indicator.signal} /></td>
      <td className="ind-value">{indicator.value}</td>
      <td className="ind-detail">{indicator.detail}</td>
    </tr>
  );
}

export default function IndicatorDashboard({ data, timeLabel }) {
  const niftyChange = data.nifty_price - (data.nifty_prev_close || 0);
  const niftyChangePct = data.nifty_prev_close > 0
    ? ((niftyChange / data.nifty_prev_close) * 100)
    : 0;

  return (
    <>
      {timeLabel && (
        <div className="time-label-banner">
          Showing indicators at: <strong>{timeLabel}</strong>
        </div>
      )}

      <section className="price-cards">
        <PriceCard
          label="NIFTY 50"
          value={data.nifty_price}
          change={niftyChangePct}
          changeLabel={`% (${niftyChange >= 0 ? '+' : ''}${niftyChange.toFixed(0)} pts)`}
        />
        <PriceCard
          label="India VIX"
          value={data.vix_value}
          change={data.vix_change_pct}
          changeLabel="% intraday"
        />
        <div className="price-card day-range">
          <div className="price-label">Day Range</div>
          <div className="range-values">
            <span>L: {data.nifty_low?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            <span>H: {data.nifty_high?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="price-change neutral">
            Range: {data.intraday_range?.toFixed(0)} pts
          </div>
        </div>
        <div className="price-card prev-day">
          <div className="price-label">Previous Day</div>
          <div className="range-values">
            <span>L: {data.prev_day_low?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            <span>H: {data.prev_day_high?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="price-change neutral">
            Close: {data.nifty_prev_close?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </div>
        </div>
      </section>

      <section className="overall-bias-section">
        <div className="overall-card">
          <div className="overall-header">
            <h2>Overall Market Bias</h2>
            <SignalBadge signal={data.overall_bias} />
          </div>
          <div className="bias-stats">
            <div className="bias-stat bullish">
              <span className="bias-count">{data.bullish_count}</span>
              <span className="bias-label">Bullish</span>
            </div>
            <div className="bias-stat bearish">
              <span className="bias-count">{data.bearish_count}</span>
              <span className="bias-label">Bearish</span>
            </div>
            <div className={`trade-status ${data.trade_allowed ? 'allowed' : 'blocked'}`}>
              {data.trade_allowed ? '✅ TRADE ALLOWED' : '🚫 NO TRADE'}
            </div>
          </div>
        </div>
      </section>

      <section className="indicators-section">
        <h2>Indicator Dashboard</h2>
        <div className="table-wrapper">
          <table className="indicators-table">
            <thead>
              <tr>
                <th>Indicator</th>
                <th>Signal</th>
                <th>Value</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {data.indicators?.map((ind, idx) => (
                <IndicatorRow key={idx} indicator={ind} />
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="dma-section">
        <h2>Moving Averages</h2>
        <div className="dma-cards">
          <div className="dma-card">
            <div className="dma-label">20 DMA (Daily)</div>
            <div className="dma-value">{data.daily_20dma?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
            <SignalBadge signal={data.daily_20dma_signal} />
          </div>
          <div className="dma-card">
            <div className="dma-label">20 DMA (5 Min)</div>
            <div className="dma-value">{data.fivemin_20dma?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
          </div>
          <div className="dma-card">
            <div className="dma-label">200 DMA (5 Min)</div>
            <div className="dma-value">{data.fivemin_200dma?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
            <SignalBadge signal={data.fivemin_dma_signal} />
          </div>
          <div className="dma-card">
            <div className="dma-label">Gap</div>
            <div className="dma-value">{data.gap_points > 0 ? '+' : ''}{data.gap_points?.toFixed(0)} pts</div>
            <SignalBadge signal={data.gap_signal} />
          </div>
        </div>
      </section>

      {(data.first_15m_high > 0 || data.first_15m_low > 0) && (
        <section className="candle-section">
          <h2>First 15-Min Candle</h2>
          <div className="candle-info">
            <div className="candle-stat">
              <span className="candle-label">High</span>
              <span className="candle-value">{data.first_15m_high?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="candle-stat">
              <span className="candle-label">Low</span>
              <span className="candle-value">{data.first_15m_low?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="candle-stat">
              <span className="candle-label">Nifty</span>
              <span className="candle-value">{data.nifty_price?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
            <SignalBadge signal={data.first_15m_signal} />
          </div>
        </section>
      )}
    </>
  );
}
